import { useState } from 'react';
import { toast } from 'sonner';
import type { MediaSummary } from '@familienbaum/shared';
import { useUploadMedia } from '@/lib/queries';
import { ApiError } from '@/lib/api';
import type { PendingDuplicate } from '@/components/media/duplicate-review-dialog';

export const MAX_UPLOAD_FILES = 25;

/**
 * Was aus einem Upload zurückkommt.
 *
 * Früher war das ein `boolean`. Das reichte für die Ablagefläche („angekommen
 * oder nicht“), aber nicht für den Zuschnitt am Telefon: der will nach dem
 * Hochladen direkt weiterarbeiten – Gesichter benennen, solange man noch bei
 * der Familie sitzt – und dafür muss er wissen, *welches* Bild angekommen ist.
 */
export interface UploadOutcome {
  /** Im Archiv angekommen – oder war schon dort. */
  ok: boolean;
  /** Die neu angelegten Medien. Bei einem Doppel bleibt die Liste leer. */
  created: MediaSummary[];
}

/**
 * Der Upload-Weg, einmal – benutzt von der Ablagefläche auf der Medienseite
 * *und* vom (+) in der Tab-Leiste.
 *
 * Vorher steckte das alles in `MediaUploader`. Ein zweiter Einstieg hätte die
 * Rückmeldungen (Meldungen, Doppel-Erkennung, abgelehnte Dateien) ein zweites
 * Mal ausformuliert – und die beiden Fassungen wären auseinandergelaufen,
 * sobald jemand eine davon anfasst.
 */
export function useMediaUpload() {
  const [problems, setProblems] = useState<Array<{ filename: string; reason: string }>>([]);
  /** Uploads, die einem vorhandenen Bild ähneln und auf eine Antwort warten. */
  const [pending, setPending] = useState<PendingDuplicate[]>([]);
  const upload = useUploadMedia();

  const send = async (files: File[]): Promise<UploadOutcome> => {
    if (files.length === 0) return { ok: false, created: [] };
    if (files.length > MAX_UPLOAD_FILES) {
      toast.error(`Höchstens ${MAX_UPLOAD_FILES} Dateien pro Vorgang`);
      return { ok: false, created: [] };
    }
    setProblems([]);

    try {
      const result = await upload.mutateAsync({ files });
      if (result.created.length > 0) {
        toast.success(
          `${result.created.length} ${result.created.length === 1 ? 'Datei' : 'Dateien'} hochgeladen – Vorschaubilder werden erzeugt.`,
        );
      }
      if (result.duplicates.length > 0) {
        toast.info(
          `${result.duplicates.length} ${result.duplicates.length === 1 ? 'Datei war' : 'Dateien waren'} bereits im Archiv.`,
        );
      }
      if (result.failed.length > 0) {
        setProblems(result.failed);
        toast.error(`${result.failed.length} Datei(en) abgelehnt.`);
      }
      // Die Datei aus dem Stapel dazulegen: der Dialog zeigt sie als Vorschau,
      // ohne sie dafür wieder vom Server holen zu müssen.
      if (result.similar.length > 0) {
        setPending(
          result.similar.map((hit) => ({
            hit,
            file: files.find((file) => file.name === hit.filename),
          })),
        );
      }
      return {
        ok:
          result.created.length > 0 ||
          result.duplicates.length > 0 ||
          result.similar.length > 0,
        created: result.created,
      };
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Upload fehlgeschlagen');
      return { ok: false, created: [] };
    }
  };

  return {
    send,
    isPending: upload.isPending,
    problems,
    pending,
    clearPending: () => setPending([]),
  };
}
